import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './styles.scss';
import { useClickOutside } from 'common/hooks';
import { useTranslation } from 'react-i18next';
import { useSpeechRecognition } from './useSpeechRecognition';
import { useAudioRecorder } from './useAudioRecorder';
import { useChatbotMessages } from './useChatbotMessages';
import { ChatbotUI } from './ChatbotUI';

export const ChatBot = () => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [inputText, setInputText] = useState('');

  const messagesEndRef = useRef(null);

  const chatboxRef = useClickOutside(() => {
    if (isOpen) setIsOpen(false);
  });

  const ERROR_MESSAGES = useMemo(
    () => ({
      NotAllowedError: t('microphone_not_allowed'),
      PermissionDeniedError: t('microphone_not_allowed'),
      NotFoundError: t('microphone_not_found'),
      NotReadableError: t('microphone_not_readable'),
      default: t('microphone_default_error'),
    }),
    [t]
  );

  // Custom hooks
  const { convertAudioToText } = useSpeechRecognition();
  const { messages, isProcessing, isSpeaking, handleSendMessage } =
    useChatbotMessages();

  const handleAudioStop = useCallback(
    async (audioBlob) => {
      console.log('Audio recording stopped, blob size:', audioBlob.size);

      if (audioBlob && audioBlob.size > 0) {
        const text = await convertAudioToText(audioBlob);
        if (text) {
          setInputText('');
          await handleSendMessage({ text, isVoice: true });
        }
      } else {
        console.warn('Empty audio blob received');
      }
    },
    [convertAudioToText, handleSendMessage]
  );

  const { isRecording, toggleVoiceInput } = useAudioRecorder(
    handleAudioStop,
    ERROR_MESSAGES
  );

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage({ text: inputText });
        setInputText('');
      }
    },
    [handleSendMessage, inputText]
  );

  const toggleChat = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleInputChange = useCallback((e) => {
    setInputText(e.target.value);
  }, []);

  const onSendMessage = useCallback(() => {
    handleSendMessage({ text: inputText });
    setInputText('');
  }, [handleSendMessage, inputText]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Scroll to end when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
      }, 100);
    }
  }, [isOpen]);

  return (
    <ChatbotUI
      isOpen={isOpen}
      messages={messages}
      inputText={inputText}
      isRecording={isRecording}
      isProcessing={isProcessing}
      isSpeaking={isSpeaking}
      messagesEndRef={messagesEndRef}
      chatboxRef={chatboxRef}
      onToggleChat={toggleChat}
      onInputChange={handleInputChange}
      onKeyPress={handleKeyPress}
      onSendMessage={onSendMessage}
      onToggleVoice={toggleVoiceInput}
    />
  );
};
